import { z } from "zod";
import type { PillowShape } from "./enums.js";
import { PROJECT_CONFIG_SIZES, ProjectConfig, ProjectSizeCm } from "./project-config.js";
import { type PillowVariantSizeCode, THREE_D_PRINT_COLOR_DEPTHS } from "./variants.js";

// The catalog kinds a Product can be — the **Product type** (ADR-0054). Data-driven
// and admin-editable (the `product_types` table), but the literal set is owned here so
// the discriminated-union config below and every api/frontend switch stay in lockstep.
// Launch = **two custom types**: the producer-made **pillow** (#402) and the operator-
// printed **3D print** ("obraz 3D", #407). Adding a type to the commerce layer is a
// literal here + a catalog row + that type's bespoke build, never a reshape of the
// commerce plumbing.
export const PRODUCT_TYPES = ["pillow", "3d_print"] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];
export const ProductTypeSchema = z.enum(PRODUCT_TYPES);

// A Product type's fulfillment path (ADR-0054 §1): a `producer`-made item shipped to
// the operator (the pillow → InPost) or a `self`-printed item the operator makes (the
// future 3D print). Bound into the `product_types.fulfillment_path` CHECK on the api
// side; shared so admin + api agree on the two allowed values.
export const FULFILLMENT_PATHS = ["producer", "self"] as const;
export type FulfillmentPath = (typeof FULFILLMENT_PATHS)[number];
export const FulfillmentPathSchema = z.enum(FULFILLMENT_PATHS);

// A **Product**'s per-type `config` (ADR-0054): the free, non-priced customization,
// validated by a Zod **discriminated union on `product_type`** so each type owns its
// own config shape. A pillow's config is the existing `ProjectConfig` fields (pet name
// / font / pattern / background + the committed shape / size / dimensions) tagged with
// the type; `resolveVariant` reads its `shape` + `size` to price it. Adding a type is a
// new branch here + that type's bespoke build — the commerce plumbing never reshapes.
export const PillowProductConfig = z.object({
  product_type: z.literal("pillow"),
  ...ProjectConfig.shape,
});
export type PillowProductConfig = z.infer<typeof PillowProductConfig>;

// A **3D print**'s per-type `config` (ADR-0054 §1, #407): DELIBERATELY not the pillow's
// fields — a 3D print carries NO pet name / font / pattern / fabric (the ADR's rejected
// "config shared on the project" alternative). It carries only what its own product needs:
// the priced axes it committed on (`size` + `color_depth`, mirrored here so a line reads
// without joining the variant), the one free, non-priced customization the buyer picks
// (the `frame`), and the committed footprint (`size_cm`). The generation pipeline
// (heightmap / 3MF) is a separate track — nothing here describes it.
export const THREE_D_PRINT_FRAMES = ["none", "black", "oak"] as const;
export const ThreeDPrintFrameSchema = z.enum(THREE_D_PRINT_FRAMES);
export type ThreeDPrintFrame = (typeof THREE_D_PRINT_FRAMES)[number];

export const ThreeDPrintProductConfig = z.object({
  product_type: z.literal("3d_print"),
  size: z.enum(PROJECT_CONFIG_SIZES),
  color_depth: z.enum(THREE_D_PRINT_COLOR_DEPTHS),
  frame: ThreeDPrintFrameSchema,
  // Null until a footprint is committed (mirrors the pillow's `size_cm`); the 3D-print
  // create path always stamps the resolved variant's nominal footprint.
  size_cm: ProjectSizeCm.nullish(),
});
export type ThreeDPrintProductConfig = z.infer<typeof ThreeDPrintProductConfig>;

export const ProductConfig = z.discriminatedUnion("product_type", [
  PillowProductConfig,
  ThreeDPrintProductConfig,
]);
export type ProductConfig = z.infer<typeof ProductConfig>;

// The Variant a buyer picks in the CART (ADR-0054 §2, #405; ADR-0056 #1, #425) — a
// discriminated union on `shape`, because the two shapes are priced on genuinely different
// axes:
//   - **silhouette** is a CONTINUOUS size: the buyer picks an EXACT footprint (`dimensionsCm`)
//     on the cart's cm slider, exactly as the preview commits one. The client sends ONLY the
//     cm; the server derives the S/L price tier from it (`silhouetteTierForDimsCm`) and never
//     trusts a client-sent tier, so pricing stays server-authoritative (ADR-0048/0054).
//   - **rectangle** is genuinely DISCRETE: three fixed footprints keyed by the S/M/L tier, so
//     the selection carries that tier and the server maps it 1:1 to the catalogue footprint.
// Used by both "add more" (`ProductCreateRequest`) and the in-cart variant edit
// (`ProductPatchRequest`); in both, the server resolves the seeded variant + its price + the
// stored footprint (`config.size_cm`) — the client NEVER sends a price.
const SilhouetteVariantSelection = z.object({
  shape: z.literal("silhouette"),
  dimensionsCm: ProjectSizeCm,
});
const RectangleVariantSelection = z.object({
  shape: z.literal("rectangle"),
  size: z.enum(PROJECT_CONFIG_SIZES),
});
export const PillowVariantSelection = z.discriminatedUnion("shape", [
  SilhouetteVariantSelection,
  RectangleVariantSelection,
]);
export type PillowVariantSelection = z.infer<typeof PillowVariantSelection>;

// The Variant a buyer picks for a **3D print** in its (stubbed) configurator (ADR-0054 §1,
// #407) — the priced axes ONLY (size + colour depth). The server resolves the seeded
// variant + its price + the nominal footprint from this pick; the client NEVER sends a
// price. The pillow's `shape` has no 3D-print analogue — a different type prices on
// different axes (the whole point of the generic `attributes`).
export const ThreeDPrintVariantSelection = z.object({
  size: z.enum(PROJECT_CONFIG_SIZES),
  color_depth: z.enum(THREE_D_PRINT_COLOR_DEPTHS),
});
export type ThreeDPrintVariantSelection = z.infer<typeof ThreeDPrintVariantSelection>;

// Request of the 3D-print **create** (`POST /products/3d-print`, ADR-0054 §1, #407): spawn
// a 3D-print **Product** on a project the caller owns, from its (stubbed) configurator —
// the buyer's Variant pick (size + colour depth) + the one free customization (`frame`).
// This is the 3D print's own bespoke create path (mirroring the pillow's `commitCheckoutSelection`),
// NOT the generic pillow "add more"; the server resolves the price + footprint server-side.
// The preview/generation artifact is STUBBED — the Product reuses the project's thumbnail
// and no pipeline runs (a separate track, ADR-0054 out of scope).
export const ThreeDPrintCreateRequest = z.object({
  projectId: z.guid(),
  variant: ThreeDPrintVariantSelection,
  frame: ThreeDPrintFrameSchema.default("none"),
});
export type ThreeDPrintCreateRequest = z.infer<typeof ThreeDPrintCreateRequest>;

// Request of the cart's **"add more"** (ADR-0054 §2, #405): spawn ANOTHER Product of
// the same type on the same `projectId`, its free customization copied from a sibling
// Product of that (project, type), the buyer's chosen `variant` applied. No preview —
// the server derives the price + footprint (`config.size_cm`) server-side. The new row
// is DISTINCT (never an upsert over the sibling, unlike the single-product "Zamów"
// get-or-create). Kept generic on `productType` so a second type slots in.
export const ProductCreateRequest = z.object({
  projectId: z.guid(),
  productType: ProductTypeSchema,
  variant: PillowVariantSelection,
});
export type ProductCreateRequest = z.infer<typeof ProductCreateRequest>;

export const ProductCreateResponse = z.object({
  productId: z.guid(),
});
export type ProductCreateResponse = z.infer<typeof ProductCreateResponse>;

// Request of the cart's **variant edit** (ADR-0054 §2, #405): change a Product's
// Variant in place (`PATCH /products/:id`) with no preview — only the priced axes
// change; the free customization (pet name / font / pattern / background) stays. The
// server re-resolves the price + footprint from the pick, so the cart price updates
// server-authoritatively.
export const ProductPatchRequest = z.object({
  variant: PillowVariantSelection,
});
export type ProductPatchRequest = z.infer<typeof ProductPatchRequest>;

export const ProductPatchResponse = z.object({
  productId: z.guid(),
});
export type ProductPatchResponse = z.infer<typeof ProductPatchResponse>;

// The server-resolved variant a config is stamped with (ADR-0054 §2, #405; #425): the
// AUTHORITATIVE pricing tier the api derived — silhouette from the committed `dimensionsCm`
// via `silhouetteTierForDimsCm`, rectangle mapped 1:1 — plus the exact footprint to persist.
// Tier derivation moved OUT of this pure helper into the api's server-authoritative
// resolution (`resolveSelectionVariant`), so a client-sent tier can never reach the config.
export interface ResolvedPillowVariant {
  shape: PillowShape;
  sizeCode: PillowVariantSizeCode;
  dimensionsCm: ProjectSizeCm;
}

// Copy a sibling Product's `config` onto a RESOLVED Variant, WITHIN the same type (ADR-0054
// §2, #405 "add more" + variant edit). The free, non-priced customization (pet name / font /
// pattern / background / material) is preserved; only the priced axes (`shape` / `size`) and
// the footprint (`size_cm`) change to the resolved variant. The tier + footprint are the ones
// the api resolved server-side (`ResolvedPillowVariant`), so this helper is a pure STAMP — it
// no longer re-derives the tier (that would double-own the S/L rule the server already
// applied). Throws if the config's `product_type` doesn't match `productType` — config is
// per-type-shaped, so it is NEVER copied across types (ADR-0054 rejected alt: "config shared
// on the project"). The result is re-validated through the discriminated union.
export function applyVariantToConfig(
  config: ProductConfig,
  productType: ProductType,
  resolved: ResolvedPillowVariant,
): ProductConfig {
  if (config.product_type !== productType) {
    throw new Error(
      `refusing to copy a "${config.product_type}" config onto a "${productType}" product — config is per-type (ADR-0054)`,
    );
  }
  switch (config.product_type) {
    case "pillow":
      return ProductConfig.parse({
        ...config,
        shape: resolved.shape,
        size: resolved.sizeCode,
        size_cm: resolved.dimensionsCm,
      });
    // A 3D print is CREATED via its own bespoke configurator (`POST /products/3d-print`,
    // which builds its config directly — like the pillow's "Zamów" commit), not this
    // sibling-copy helper. The cart's no-preview "add more"/edit-variant is pillow-only in
    // the MVP (ADR-0054); a 3D-print `selection` here would be shape-shaped, which it is
    // not — so this refuses rather than fabricate a nonsense pillow axis. Reachable only
    // past the cross-type guard above (config + productType both 3d_print), i.e. never
    // from the pillow cart.
    case "3d_print":
      throw new Error(
        "applyVariantToConfig does not support 3d_print — a 3D print is created via its configurator (ADR-0054, #407)",
      );
  }
}
